import { defineModule } from '../utils/helpers';

/**
 * The hero's match inbox: First2Find match-mails landing in a recruiter's
 * mailbox, one every few seconds, up to three — then the inbox clears and the
 * cycle starts over.
 *
 * PHP renders every state's markup and leaves the stage on the last one (all
 * matches in, the newest open), so the block editor and anyone who asked for
 * reduced motion get a meaningful frame without this file. All that happens
 * here is rewinding to an empty inbox and stepping a counter: which rows are
 * collapsed, which mail is open and how old each match reads are entirely a
 * matter of classes, styled in hero-header/styles.css.
 */
const STEP = 3000;

/**
 * How long the inbox sits empty before the first match lands — on setup, and
 * again every time the cycle wraps. Shorter than STEP: an empty postvak is the
 * setup for the story rather than a beat of it, and a mailbox with no matches in
 * it is the one thing this banner should not be caught showing. Long enough that
 * the reading pane's cross-dissolve (900ms) finishes first.
 */
const EMPTY_STEP = 1400;

/** Matches `.matchbox__row--match`'s height transition in hero-header/styles.css. */
const COLLAPSE = 620;

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

const getElements = (root: HTMLElement) => ({
  rows: Array.from(
    root.querySelectorAll<HTMLElement>('[data-matchbox-match]'),
  ).sort(
    (a, b) => Number(a.dataset.matchboxMatch) - Number(b.dataset.matchboxMatch),
  ),
  panes: Array.from(root.querySelectorAll<HTMLElement>('[data-matchbox-pane]')),
  screen: root.querySelector<HTMLElement>('[data-matchbox-screen]'),
  mails: Array.from(root.querySelectorAll<HTMLElement>('[data-matchbox-mail]')),
  restingRow: root.querySelector<HTMLElement>('[data-matchbox-resting-row]'),
  counters: Array.from(
    root.querySelectorAll<HTMLElement>('[data-matchbox-count]'),
  ),
  pause: root.querySelector<HTMLButtonElement>('[data-matchbox-pause]'),
});

const setupStage = (root: HTMLElement): (() => void) => {
  const { rows, panes, screen, mails, restingRow, counters, pause } =
    getElements(root);
  if (!rows.length) return () => {};

  // How old a match reads once newer ones have landed on top of it; index 0 is
  // the one that just came in. Written by PHP so no copy lives in this file.
  const ages = (root.dataset.matchboxAges ?? '').split('|').filter(Boolean);

  /** Number of matches that have arrived: 0 is an inbox with none of them. */
  let state = rows.length;
  let timer = 0;
  let counterTimer = 0;
  let frame = 0;
  let paused = false;
  let onScreen = true;
  let running = false;

  const renderCounters = (): void => {
    counters.forEach((counter) => {
      const base = Number(counter.dataset.matchboxCount);

      counter.textContent = String((Number.isFinite(base) ? base : 0) + state);
    });
  };

  const render = (previous = state): void => {
    rows.forEach((row, index) => {
      const arrived = index < state;
      const newest = index === state - 1;

      row.classList.toggle('matchbox__row--collapsed', !arrived);
      row.classList.toggle('matchbox__row--active', newest);
      row.classList.toggle('matchbox__row--unread', newest);

      const time = row.querySelector<HTMLElement>('[data-matchbox-time]');
      if (time && arrived) {
        time.textContent = ages[Math.min(state - 1 - index, ages.length - 1)];
      }
    });

    restingRow?.classList.toggle('matchbox__row--active', state === 0);

    const open = state === 0 ? 'resting' : String(state - 1);
    panes.forEach((pane) =>
      pane.classList.toggle(
        'matchbox__pane--open',
        pane.dataset.matchboxPane === open,
      ),
    );

    // A mail that lands is in the inbox from the moment it appears, so the rail
    // counts move with it. Emptying runs the other way round: the rows are still
    // on screen while they collapse, and a count that already dropped
    // contradicts what is being looked at.
    window.clearTimeout(counterTimer);

    if (state >= previous) renderCounters();
    else counterTimer = window.setTimeout(renderCounters, COLLAPSE);
  };

  const stop = (): void => {
    window.clearTimeout(timer);
    timer = 0;
  };

  const delayFor = (): number => (state === 0 ? EMPTY_STEP : STEP);

  const schedule = (delay: number): void => {
    stop();
    timer = window.setTimeout(() => {
      const previous = state;

      // Past the last match the inbox empties and the cycle starts over.
      state = (state + 1) % (rows.length + 1);
      render(previous);
      schedule(delayFor());
    }, delay);
  };

  const sync = (): void => {
    if (paused || !onScreen) stop();
    else if (!timer) schedule(delayFor());
  };

  let disposed = false;
  let fittedAt = 0;

  const fit = (): void => {
    if (!screen || !mails.length) return;

    root.style.removeProperty('--matchbox-fit');

    const { height } = screen.getBoundingClientRect();
    if (!height) return;

    const shortfall = mails.reduce(
      (most, mail) => Math.max(most, mail.scrollHeight - mail.clientHeight),
      0,
    );

    if (shortfall > 0) {
      root.style.setProperty(
        '--matchbox-fit',
        ((height + shortfall) / height).toFixed(4),
      );
    }
  };

  const refit = (): void => {
    const width = mails[0]?.clientWidth ?? 0;
    if (disposed || width === fittedAt) return;

    fittedAt = width;
    fit();
  };

  const resize = new ResizeObserver(refit);

  const onPause = (): void => {
    paused = !paused;
    root.classList.toggle('matchbox--paused', paused);
    pause?.setAttribute(
      'aria-label',
      (paused ? pause.dataset.labelPlay : pause.dataset.labelPause) ?? '',
    );
    sync();
  };

  const observer = new IntersectionObserver(
    ([entry]) => {
      onScreen = entry.isIntersecting;
      if (running) sync();
    },
    { threshold: 0.15 },
  );

  const start = (): void => {
    if (running) return;
    running = true;

    // Rewind to an empty inbox without animating the rewind itself: the guard
    // class only comes off once the browser has committed the emptied state, so
    // the two never end up in the same style recalculation.
    root.classList.add('matchbox--instant');
    state = 0;
    render();

    frame = window.requestAnimationFrame(() => {
      frame = window.requestAnimationFrame(() =>
        root.classList.remove('matchbox--instant'),
      );
    });

    pause?.addEventListener('click', onPause);
    pause?.classList.remove('matchbox__pause--idle');
    observer.observe(root);
    schedule(delayFor());
  };

  /**
   * Come to rest on the frame PHP rendered: every match in, the newest one open.
   * Nothing moves after this, so there is nothing to offer a pause button for.
   */
  const settle = (): void => {
    running = false;
    paused = false;
    stop();
    window.clearTimeout(counterTimer);
    window.cancelAnimationFrame(frame);
    observer.disconnect();
    pause?.removeEventListener('click', onPause);
    pause?.classList.add('matchbox__pause--idle');
    root.classList.remove('matchbox--paused', 'matchbox--instant');

    state = rows.length;
    render();
  };

  // Read as a live query rather than once at load: someone who turns the
  // preference on mid-visit is asking for the motion to stop now, not next
  // navigation.
  const onPreferenceChange = (): void => {
    if (reducedMotion.matches) settle();
    else start();
  };

  reducedMotion.addEventListener('change', onPreferenceChange);
  onPreferenceChange();

  if (screen) resize.observe(screen);

  document.fonts?.ready.then(() => {
    if (!disposed) fit();
  });

  return () => {
    disposed = true;
    resize.disconnect();
    root.style.removeProperty('--matchbox-fit');
    reducedMotion.removeEventListener('change', onPreferenceChange);
    settle();
  };
};

let cleanups: Array<() => void> = [];

export default defineModule(
  () => {
    cleanups = Array.from(
      document.querySelectorAll<HTMLElement>('[data-matchbox]'),
    ).map(setupStage);
  },
  () => {
    cleanups.forEach((fn) => fn());
    cleanups = [];
  },
);
